import {useLayoutEffect, useRef} from "react";
import {Box} from "@mui/material";
import {Colors} from "../../colors";
import {highlightHtml} from "./highlightHtml";

const FONT_FAMILY = 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';
const FONT_SIZE = 13;
const LINE_HEIGHT = 1.6;
const PADDING = 12;
const TAB_SIZE = 2;
const MAX_ROWS = 24;
const SELECTION_COLOR = "rgba(37, 97, 216, 0.24)";

type HtmlCodeEditorProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  minRows?: number;
  placeholder?: string;
  error?: boolean;
  inputRef?: React.Ref<HTMLTextAreaElement>;
};

const rowsToHeight = (rows: number) => rows * FONT_SIZE * LINE_HEIGHT + PADDING * 2;

const HtmlCodeEditor = ({value, onChange, onBlur, minRows = 6, placeholder, error, inputRef}: HtmlCodeEditorProps) => {
  const preRef = useRef<HTMLPreElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const textStyles = {
    margin: 0,
    padding: `${PADDING}px`,
    fontFamily: FONT_FAMILY,
    fontSize: `${FONT_SIZE}px`,
    lineHeight: LINE_HEIGHT,
    fontVariantLigatures: "none",
    tabSize: TAB_SIZE,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    border: 0,
  } as const;

  const highlighted = highlightHtml(value.endsWith("\n") ? `${value}\n` : value);
  const minHeight = rowsToHeight(minRows);
  const maxHeight = rowsToHeight(MAX_ROWS);

  // Mirror the textarea's scroll onto the highlight layer so the two stay aligned
  // once the content grows past maxHeight and the textarea starts scrolling.
  const syncScroll = () => {
    if (preRef.current && textareaRef.current) {
      preRef.current.style.transform = `translateY(${-textareaRef.current.scrollTop}px)`;
    }
  };

  useLayoutEffect(syncScroll);

  const setTextareaRef = (el: HTMLTextAreaElement | null) => {
    textareaRef.current = el;
    if (typeof inputRef === "function") inputRef(el);
    else if (inputRef) (inputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: "8px",
        border: `1px solid ${error ? Colors.red._800 : Colors.tertiary._300}`,
        backgroundColor: Colors.bw.white,
        overflow: "hidden",
        minHeight,
        maxHeight,
        "&:focus-within": {borderColor: error ? Colors.red._800 : Colors.blue._600},
      }}
    >
      <Box
        component="pre"
        ref={preRef}
        aria-hidden
        sx={{...textStyles, position: "relative", minHeight, color: Colors.grey._800, overflow: "hidden"}}
        dangerouslySetInnerHTML={{__html: highlighted}}
      />
      <Box
        component="textarea"
        ref={setTextareaRef}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
        onBlur={onBlur}
        onScroll={syncScroll}
        placeholder={placeholder}
        spellCheck={false}
        sx={{
          ...textStyles,
          position: "absolute",
          inset: 0,
          display: "block",
          width: "100%",
          height: "100%",
          resize: "none",
          background: "transparent",
          color: "transparent",
          caretColor: Colors.grey._900,
          outline: "none",
          overflow: "auto",
          "&::placeholder": {color: Colors.grey._400},
          "&::selection": {backgroundColor: SELECTION_COLOR},
        }}
      />
    </Box>
  );
};

export default HtmlCodeEditor;
